import React from "react";
import { Button } from "@/components";
import {
  Edit,
  Trash2,
  ArrowLeft,
  Calendar,
  Image as ImageIcon,
  Video,
  Info,
  HelpCircle,
} from "lucide-react";
import type { Brand } from "../interfaces";

interface BrandDetailProps {
  brand: Brand;
  onEdit: (brand: Brand) => void;
  onDelete: (brandId: number) => void;
  onBack: () => void;
  onDeleteSuccess?: () => void;
}

export const BrandDetail: React.FC<BrandDetailProps> = ({
  brand,
  onEdit,
  onDelete,
  onBack,
}) => {
  const handleDelete = () => {
    onDelete(brand.id);
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              title="Back to list"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold">{brand.title}</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onEdit(brand)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>
              Created: {new Date(brand.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>
              Updated: {new Date(brand.updatedAt).toLocaleDateString()}
            </span>
          </div>
          {brand.strategyIds && brand.strategyIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {brand.strategyIds.map((id) => (
                <span
                  key={id}
                  className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium"
                >
                  Plan #{id}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="bg-card border border-input rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-3">Description</h2>
        <p className="text-muted-foreground whitespace-pre-wrap">
          {brand.description}
        </p>
      </div>

      {/* Images */}
      {brand.images && brand.images.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Images ({brand.images.length})
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {brand.images.map((image, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg overflow-hidden bg-muted group cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              >
                <img
                  src={image}
                  alt={`${brand.title} image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  onClick={() => window.open(image, "_blank")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {brand.videos && brand.videos.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Video className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Videos ({brand.videos.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {brand.videos.map((video, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg overflow-hidden bg-muted"
              >
                {video.includes("youtube.com/embed") ||
                video.includes("youtu.be") ? (
                  <iframe
                    src={video}
                    title={`${brand.title} video ${index + 1}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={video} controls className="w-full h-full">
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brand Info (product/service info) */}
      {brand.config?.product_or_service_info &&
        brand.config.product_or_service_info.length > 0 && (
          <div className="bg-card border border-input rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="h-5 w-5" />
              <h2 className="text-xl font-semibold">Brand Information</h2>
            </div>
            <ul className="space-y-2">
              {brand.config.product_or_service_info.map((info, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-muted-foreground flex-1">{info}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

      {/* FAQs */}
      {brand.config?.faq && brand.config.faq.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Frequently Asked Questions ({brand.config.faq.length})
            </h2>
          </div>
          <div className="space-y-4">
            {brand.config.faq.map((faq, index) => (
              <div key={index} className="border-l-4 border-primary pl-4">
                <h3 className="font-semibold mb-2">{faq.question}</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State if no additional content */}
      {(!brand.images || brand.images.length === 0) &&
        (!brand.videos || brand.videos.length === 0) &&
        (!brand.config?.product_or_service_info ||
          brand.config.product_or_service_info.length === 0) &&
        (!brand.config?.faq || brand.config.faq.length === 0) && (
          <div className="bg-card border border-input rounded-lg p-12 text-center">
            <p className="text-muted-foreground">
              No additional content available for this brand.
            </p>
          </div>
        )}
    </div>
  );
};
